import type { QueryClient } from '@tanstack/react-query';
import type { LoaderFunctionArgs, RouteObject } from 'react-router';
import { mySettingsQuery, waitingQuery } from '../api';
import { payrollQuery, shareworksQuery, statementsQuery } from '../api/close';
import { requirePage } from '../app/access';
import type { RouteHandle } from '../app/RootLayout';
import { movedTo } from '../manage/moved';
import { MANAGE_PAGE } from '../manage/pages';
import { WORK_HREF } from '../ops/labels';
import { forRoles } from '../search/pages';
import { skipRevalidationFor } from '../ui/revalidate';
import { PayrollPage } from './payroll/PayrollPage';
import { SHAREWORKS_PARAMS } from './shareworks/presenter';
import { ShareworksPage } from './shareworks/ShareworksPage';
import { STATEMENTS_PARAMS } from './statements/presenter';
import { StatementsPage } from './statements/StatementsPage';

const payrollHandle: RouteHandle = {
  help: { pageLabel: 'Payroll queue', topicIds: ['waiting', 'in-progress'] },
  search: {
    label: 'Payroll queue',
    meta: 'Lines to pay and earning statements to attach',
    visible: forRoles('payroll'),
  },
};
const statementsHandle: RouteHandle = {
  help: { pageLabel: 'Earning statements · Stock Admin', topicIds: ['queue', 'in-progress'] },
  search: {
    label: 'Earning statements',
    meta: 'What Payroll paid against the app’s estimate',
    visible: forRoles('stock_admin'),
  },
};
/**
 * Earning statements and Shareworks come whole and page on the screen (APP-135 §4), and sort and
 * filter there too (APP-149): a new page, rows per page, sort or filter leaves the loaders alone,
 * so the list changes at once.
 */
const statementsOnScreen = skipRevalidationFor(['page', 'size', ...STATEMENTS_PARAMS]);
const shareworksOnScreen = skipRevalidationFor(['page', 'size', ...SHAREWORKS_PARAMS]);
/**
 * Payroll's loader reads only the summary; the page fetches each page itself, holding the rows in
 * view with "Loading page N…". A new page or rows per page (a saved size leaving the address too)
 * leaves the loader alone, so it is that one request, never the summary first. A view, period or
 * date change still runs it. APP-248: the Spot section's pager is the same.
 */
const payrollPaging = skipRevalidationFor(['page', 'size', 'spotPage', 'spotSize']);

const shareworksHandle: RouteHandle = {
  help: { pageLabel: 'Shareworks worklist · Stock Admin', topicIds: ['queue', 'in-progress'] },
  search: {
    label: 'Shareworks worklist',
    meta: 'Option and share grants to enter in Shareworks',
    visible: forRoles('stock_admin'),
  },
};

/**
 * After the letters (APP-043): Payroll's queue (P1), the earning statement check (SA6) and the
 * Shareworks worklist (SA5), Manage tabs since APP-234. Children of the signed-in layout; each is
 * for its own role, and System Admins see them all with nothing to change (APP-134).
 */
export function closeRoutes(queryClient: QueryClient): RouteObject[] {
  return [
    // APP-234: their addresses before Manage still land.
    movedTo('payroll', MANAGE_PAGE.payroll.href),
    movedTo('work/earning-statements', WORK_HREF.earning_statements),
    movedTo('work/shareworks', WORK_HREF.shareworks),
    {
      path: MANAGE_PAGE.payroll.href.slice(1),
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requirePage(queryClient, request, ['payroll'], 'Payroll');
        await Promise.all([queryClient.query(payrollQuery), queryClient.query(waitingQuery)]);
        return null;
      },
      shouldRevalidate: payrollPaging,
      Component: PayrollPage,
      handle: payrollHandle,
    },
    {
      path: WORK_HREF.earning_statements.slice(1),
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requirePage(queryClient, request, ['stock_admin'], 'the Stock Admin team');
        // A kept sort is the person's setting (APP-149 D7), read first so the list opens in it.
        await Promise.all([
          queryClient.query(statementsQuery),
          queryClient.query(mySettingsQuery).catch(() => undefined),
        ]);
        return null;
      },
      shouldRevalidate: statementsOnScreen,
      Component: StatementsPage,
      handle: statementsHandle,
    },
    {
      path: WORK_HREF.shareworks.slice(1),
      loader: async ({ request }: LoaderFunctionArgs) => {
        await requirePage(queryClient, request, ['stock_admin'], 'the Stock Admin team');
        await Promise.all([
          queryClient.query(shareworksQuery),
          queryClient.query(mySettingsQuery).catch(() => undefined),
        ]);
        return null;
      },
      shouldRevalidate: shareworksOnScreen,
      Component: ShareworksPage,
      handle: shareworksHandle,
    },
  ];
}
